import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { Order } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, getOrderStatusTranslation } from '../utils/translations';

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());
  const ordersPerPage = 12;
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);

  useEffect(() => {
    fetchOrders();
    
    const subscription = supabase
      .channel('admin_orders_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'habib_orders' }, (payload) => {
        console.log('Orders table change:', payload);
        fetchOrders();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'habib_order_items' }, (payload) => {
        console.log('Order items table change:', payload);
        fetchOrders();
      })
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    console.log('Fetching orders...');
    
    const { data, error } = await supabase
      .from('habib_orders')
      .select(`
        *,
        items:habib_order_items(
          *,
          product:habib_products(*),
          menu:habib_menu(*)
        )
      `)
      .order('created_at', { ascending: false });
    
    console.log('Orders query result:', { data, error });
    
    if (error) {
      console.error('Error fetching orders:', error);
    }
    
    if (data) {
      console.log('Setting orders:', data);
      setOrders(data);
    }
    setLoading(false);
  };

  const updateOrderStatus = async (orderId: number, status: 'Permintaan Pesanan' | 'Pesanan Selesai') => {
    const order = orders.find(o => o.id === orderId);
    
    // If marking as complete and order was previously pending, reduce stock
    if (status === 'Pesanan Selesai' && order?.status === 'Permintaan Pesanan' && order.items) {
      console.log('Reducing stock for order:', orderId);
      
      // Reduce stock for each item in the order
      for (const item of order.items) {
        if (item.product_id) {
          // Get current stock from database
          const { data: product } = await supabase
            .from('habib_products')
            .select('stock')
            .eq('id', item.product_id)
            .single();
          
          if (product) {
            const currentStock = product.stock;
            const newStock = Math.max(0, currentStock - item.quantity);
            
            console.log(`Product ${item.product_id}: ${currentStock} -> ${newStock} (reducing by ${item.quantity})`);
            
            const { error } = await supabase
              .from('habib_products')
              .update({ stock: newStock })
              .eq('id', item.product_id);
              
            if (error) {
              console.error('Error updating stock:', error);
            }
          }
        }
        // Note: Menu items don't have stock tracking, so we skip them
      }
    }
    
    // Update order status
    const { error } = await supabase
      .from('habib_orders')
      .update({ status })
      .eq('id', orderId);
      
    if (error) {
      console.error('Error updating order status:', error);
    }
    
    fetchOrders();
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const toggleSelectOrder = (orderId: number) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === currentOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(currentOrders.map(o => o.id)));
    }
  };

  const bulkDeleteOrders = async () => {
    if (selectedOrders.size === 0 || !window.confirm(`Delete ${selectedOrders.size} order(s)?`)) return;
    
    const orderIds = Array.from(selectedOrders);
    for (const orderId of orderIds) {
      await supabase.from('habib_order_items').delete().eq('order_id', orderId);
      await supabase.from('habib_orders').delete().eq('id', orderId);
    }
    
    setSelectedOrders(new Set());
    fetchOrders();
  };

  const filteredOrders = orders.filter(order => {
    const matchesFilter = (() => {
      if (filter === 'pending') return order.status === 'Permintaan Pesanan';
      if (filter === 'completed') return order.status === 'Pesanan Selesai';
      return true;
    })();
    
    const matchesSearch = searchTerm === '' || 
      order.id.toString().includes(searchTerm) ||
      order.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.total_items.toString().includes(searchTerm);
    
    return matchesFilter && matchesSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const currentOrders = filteredOrders.slice(startIndex, startIndex + ordersPerPage);

  const toggleOrderExpansion = (orderId: number) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getStatusIcon = (status: string) => {
    if (status === 'Pesanan Selesai') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">{t('orderManagement')}</h2>
          <p className="text-slate-600 text-sm sm:text-base">{t('manageTrackOrders')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-slate-600">{t('realtimeUpdates')}</span>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-4 sm:p-6" style={{borderColor: '#05472A20'}}>
        <div className="relative max-w-md">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('searchOrders')}
            className="w-full px-4 py-3 pl-12 pr-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
            style={{borderColor: '#05472A20'}}
          />
          <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedOrders.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl shadow-soft p-4" style={{borderColor: '#dc2626'}}>
          <div className="flex justify-between items-center">
            <span className="text-red-700 font-semibold">
              {selectedOrders.size} selected
            </span>
            <button
              onClick={bulkDeleteOrders}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-4 sm:p-6" style={{borderColor: '#05472A20'}}>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {setFilter('all'); setCurrentPage(1);}}
            className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
              filter === 'all'
                ? 'text-white'
                : 'bg-white border hover:bg-green-50'
            }`}
            style={filter === 'all' ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
          >
            {t('allOrders')} ({orders.length})
          </button>
          <button
            onClick={() => {setFilter('pending'); setCurrentPage(1);}}
            className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-white border hover:bg-yellow-50 text-yellow-700'
            }`}
            style={filter !== 'pending' ? {borderColor: '#eab308'} : {}}
          >
            {t('pending')} ({orders.filter(o => o.status === 'Permintaan Pesanan').length})
          </button>
          <button
            onClick={() => {setFilter('completed'); setCurrentPage(1);}}
            className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
              filter === 'completed'
                ? 'text-white'
                : 'bg-white border hover:bg-green-50'
            }`}
            style={filter === 'completed' ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
          >
            {t('completed')} ({orders.filter(o => o.status === 'Pesanan Selesai').length})
          </button>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="card-elevated p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('loadingOrders')}</p>
        </div>
      ) : currentOrders.length === 0 ? (
        <div className="card-elevated p-8 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('noOrdersFound')}</h3>
          <p className="text-slate-600">{t('noOrdersMatch')}</p>
          <p className="text-xs text-slate-400 mt-2">{t('totalOrders2')}: {orders.length} | {t('filtered')}: {filteredOrders.length}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All */}
          {currentOrders.length > 0 && (
            <div className="bg-white rounded-xl shadow-soft border p-3" style={{borderColor: '#05472A20'}}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedOrders.size === currentOrders.length}
                  onChange={toggleSelectAll}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <span className="font-medium" style={{color: '#05472A'}}>Select All ({currentOrders.length})</span>
              </label>
            </div>
          )}

          {currentOrders.map((order) => (
            <div key={order.id} className="card-elevated overflow-hidden">
              {/* Order Header - Clickable */}
              <div 
                className="p-4 sm:p-6 bg-gradient-to-r from-slate-50 to-slate-100 cursor-pointer hover:from-slate-100 hover:to-slate-200 transition-all duration-300"
                onClick={() => toggleOrderExpansion(order.id)}
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelectOrder(order.id);
                      }}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-sm">#{order.id}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-slate-800">{t('order')} #{order.id}</h3>
                        <button className="p-1 hover:bg-slate-200 rounded-lg transition-colors duration-200">
                          <svg 
                            className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${
                              expandedOrders.has(order.id) ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          {order.total_items} {t('items')}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 2m8-2l2 2m-2-2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V9" />
                          </svg>
                          {new Date(order.created_at).toLocaleDateString()} {new Date(order.created_at).toLocaleTimeString()}
                        </span>
                        <button 
                          className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOrderExpansion(order.id);
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {expandedOrders.has(order.id) ? t('hideItems') : t('viewItems')}
                        </button>
                      </div>
                      {order.note && (
                        <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Note:</p>
                          <p className="text-sm text-blue-900">{order.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    <span className={`status-badge flex items-center gap-2 ${
                      order.status === 'Pesanan Selesai'
                        ? 'bg-success-100 text-success-700'
                        : 'bg-warning-100 text-warning-700'
                    }`}>
                      {getStatusIcon(order.status)}
                      {getOrderStatusTranslation(order.status, language)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expandable Order Items */}
              {expandedOrders.has(order.id) && order.items && order.items.length > 0 && (
                <div className="border-t border-slate-200 bg-white">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        {t('orderItems')} ({order.items.length})
                      </h4>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOrderExpansion(order.id);
                        }}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="grid gap-3 max-h-80 overflow-y-auto">
                      {order.items.map((item, index) => {
                        const isProduct = !!item.product_id;
                        const isMenu = !!item.menu_id;
                        const photo = isProduct ? item.product?.photo : item.menu?.photo;
                        const name = isProduct ? item.product?.nama_produk : item.menu?.name;
                        const description = isProduct ? item.product?.deskripsi_produk : item.menu?.description;
                        
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl hover:from-slate-100 hover:to-slate-200 transition-all duration-300"
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <div className="relative">
                              <img 
                                src={photo} 
                                alt={name}
                                className="w-16 h-16 object-cover rounded-xl shadow-soft"
                              />
                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {item.quantity}
                              </div>
                              <div className={`absolute -bottom-1 -left-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                                isMenu ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                              }`}>
                                {isMenu ? '🍽️' : '📦'}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-800 mb-1 truncate">{name}</p>
                              <p className="text-sm text-slate-600 line-clamp-2">{description}</p>
                            </div>
                            <div className="text-right">
                              <div className="bg-white px-3 py-2 rounded-lg shadow-soft">
                                <p className="text-xs text-slate-500 mb-1">{t('quantity')}</p>
                                <p className="font-bold text-slate-800 text-lg">{item.quantity}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateOrderStatus(order.id, 'Permintaan Pesanan');
                    }}
                    disabled={order.status === 'Permintaan Pesanan'}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      order.status === 'Permintaan Pesanan'
                        ? 'bg-warning-500 text-white cursor-default'
                        : 'bg-white border border-warning-200 text-warning-700 hover:bg-warning-50 hover:border-warning-300'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('markAsPending')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateOrderStatus(order.id, 'Pesanan Selesai');
                    }}
                    disabled={order.status === 'Pesanan Selesai'}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      order.status === 'Pesanan Selesai'
                        ? 'bg-success-500 text-white cursor-default'
                        : 'bg-white border border-success-200 text-success-700 hover:bg-success-50 hover:border-success-300'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('markAsCompleted')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-2xl shadow-soft border p-4" style={{borderColor: '#05472A20'}}>
          <div className="flex justify-between items-center">
            <p className="text-sm" style={{color: '#05472A99'}}>
              Showing {startIndex + 1}-{Math.min(startIndex + ordersPerPage, filteredOrders.length)} of {filteredOrders.length}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                ←
              </button>
              <span className="px-3 py-1 font-medium" style={{color: '#05472A'}}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
